import { isPlatformBrowser } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, PLATFORM_ID, signal, inject } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { environment } from '@env/environment';
import { isBankPortalPath } from '@services/helpers/bank-portal-session.service';
import Hotjar from '@hotjar/browser';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import {
  selectUserEmail,
  selectUserFullName,
  selectUserId,
} from '@src/app/state/auth/auth.selectors';
import { Subject, filter, takeUntil, withLatestFrom } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

import { VaultApiService } from './core/services/api/vault-api.service';
import { FeedbackConsoleInterceptorService } from './core/services/helpers/feedback-console-interceptor.service';
import { RealtimeService } from './core/services/realtime/realtime.service';
import { SessionPermissionsSyncService } from './core/services/auth/session-permissions-sync.service';
import { ImpersonationSessionService } from './core/services/auth/impersonation-session.service';
import { ToastHelperService } from './core/services/helpers/toast-helper.service';
import { UtilsService } from './core/services/helpers/utils.service';
import { AuthenticationService } from './core/services/http/authentication.service';
import { TenantCompanyScopeReloadService } from './shared/services/tenant-company-scope-reload.service';
import { AppState } from './state';
import { selectLanguage } from './state/auth/auth.selectors';
import { toggleDebugInfo } from './state/global/global.actions';
import { setIsMobile } from './state/settings/settings.actions';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  // Injetado aqui para abrir a conexão de tempo real da aba assim que houver usuário logado
  // (card 3496). O serviço cuida sozinho de login, logout, SSR e ambiente sem hub.
  private readonly realtime = inject(RealtimeService);
  // Permissões que refletem na hora (PBI 3499): troca o token e recarrega as permissões no F5 e quando o backend avisa.
  private readonly sessionPermissionsSync = inject(SessionPermissionsSyncService);
  // "Entrar como este usuário" (card 3804): ouve o aviso de saída das outras abas, trata o "Sair" e o vencimento.
  private readonly impersonationSession = inject(ImpersonationSessionService);

  title = 'Cognito - By Legatus';
  showFeedbackButton = signal(true);
  private destroy$ = new Subject<void>();
  private tenantCompanyScopeStorageListener?: (event: StorageEvent) => void;

  // `/dataroom` = portal do banco (Kit Banco): quem entra é contato do banco, não usuário da plataforma.
  private readonly publicRoutes = ['/login', '/create-password', '/dataroom'];

  constructor(
    private router: Router,
    @Inject(TranslateService) private translate: TranslateService,
    private toast: ToastHelperService,
    private utilsService: UtilsService,
    private authenticationService: AuthenticationService,
    private apiService: VaultApiService,
    private tenantCompanyScopeReloadService: TenantCompanyScopeReloadService,
    private store: Store<AppState>,
    @Inject(PLATFORM_ID) // eslint-disable-next-line @typescript-eslint/ban-types
    private platformId: Object,
    // Injetado aqui para garantir inicialização antecipada do interceptor de console
    _feedbackConsoleInterceptor: FeedbackConsoleInterceptorService
  ) {
    if (isPlatformBrowser(this.platformId)) {
      // Remove tabindex from all menus click - bug primeng https://github.com/primefaces/primeng/issues/13934
      document.body.addEventListener('click', () => {
        setTimeout(() => {
          document.querySelectorAll('.p-menu-list[tabindex]').forEach(ul => {
            ul.removeAttribute('tabindex');
          });
        }, 0);
      });

      // Keyboard shortcuts listener
      document.addEventListener('keydown', (event: KeyboardEvent) => {
        this.handleKeyboardShortcuts(event);
      });
    }
  }

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.initializeApp();
      this.sessionPermissionsSync.start();
      this.impersonationSession.start();
    }
  }

  ngOnDestroy(): void {
    if (isPlatformBrowser(this.platformId) && this.tenantCompanyScopeStorageListener) {
      window.removeEventListener('storage', this.tenantCompanyScopeStorageListener);
    }

    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeApp(): void {
    this.setupInternacionalization();
    this.setupPrimeNg();
    this.setupPrimeNgOverlayCleanup();
    this.setupFeedbackButtonVisibility();
    this.setupTenantCompanyScopeReloadSync();

    // Portal do banco (`/dataroom`): quem está na aba é contato do banco, não usuário da plataforma. Sem gravação de
    // sessão (Clarity), sem chat (Jivo), sem analytics com a identidade da plataforma e sem refresh do token dela.
    if (isBankPortalPath()) {
      return;
    }

    if (environment.production) {
      //this.loadHotJarAnalytics();
      this.loadClarityScript();
      //this.loadGoogleAnalytics();
      this.loadJivoScript();
    }

    this.setupAnalytics();

    this.authenticationService.startTokenRefreshInterval();
  }

  private setupTenantCompanyScopeReloadSync(): void {
    this.tenantCompanyScopeStorageListener = (event: StorageEvent) =>
      this.tenantCompanyScopeReloadService.reloadCurrentTabFromStorageEvent(event);
    window.addEventListener('storage', this.tenantCompanyScopeStorageListener);
  }

  private setupInternacionalization(): void {
    const fallbackLanguage = 'pt-BR';
    this.translate.setDefaultLang(fallbackLanguage);

    // Mantém o idioma reativo ao estado do usuário (usado pelo app-select-language).
    // Isso evita “fixar” pt-BR e garante que a UI atualize ao trocar o idioma.
    this.store
      .select(selectLanguage)
      .pipe(takeUntil(this.destroy$), distinctUntilChanged())
      .subscribe(language => {
        const nextLanguage = language || fallbackLanguage;
        if (this.translate.currentLang !== nextLanguage) {
          this.translate.use(nextLanguage);
        }
      });
  }

  loadJivoScript() {
    if (!environment.jivo || !environment.jivo.scriptSrc) {
      console.warn('JivoChat scriptSrc is not defined in environment');
      return;
    }

    if (document.getElementById('jivo-script')) {
      console.info('JivoChat script já adicionado');
      return;
    }

    console.info('loadJivoScript started');
    const jivoScript = document.createElement('script');
    jivoScript.async = true;
    jivoScript.src = environment.jivo.scriptSrc;
    jivoScript.id = 'jivo-script';
    document.head.appendChild(jivoScript);
  }

  private setupFeedbackButtonVisibility(): void {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(event => {
        const url = (event as NavigationEnd).urlAfterRedirects;
        const isPublic = url === '/' || this.publicRoutes.some(route => url.startsWith(route));
        this.showFeedbackButton.set(!isPublic);
      });
  }

  private setupAnalytics(): void {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        withLatestFrom(
          this.store.select(selectUserEmail),
          this.store.select(selectUserId),
          this.store.select(selectUserFullName)
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(([, email, id, fullname]) => {
        const windowType = window as any;
        if (email && id) {
          if (environment.production) {
            if (windowType.clarity) {
              windowType.clarity('set', 'user-email', email);
              windowType.clarity('set', 'user-id', id);
              windowType.clarity('identify', id, undefined, undefined, email);
              console.info('User identified on Clarity');
            }

            if (windowType.hj) {
              windowType.hj('identify', id, {
                email: email,
              });
              console.info('User identified on Hotjar');
            }

            if (windowType.gtag) {
              windowType.gtag('config', environment.googleAnalytics.trackingId, {
                user_id: id,
                user_properties: {
                  email: email,
                },
              });
              console.info('User identified on Google Analytics');
            }

            if (windowType.jivo_api && fullname && email) {
              windowType.jivo_api.setContactInfo({ name: fullname, email });
              console.info('User identified on JivoChat');
            }
          }
        }
      });
  }

  private setupPrimeNg(): void {
    // Ripple is now configured via providePrimeNG() in app.module.ts
    const isMobile = this.utilsService.isMobile();
    this.store.dispatch(setIsMobile({ isMobile }));
  }

  /**
   * PrimeNG usa overlays com appendTo="body" (p-menu, p-tieredMenu, etc.).
   * Se o usuário navegar sem fechar, o overlay pode “vazar” para a próxima rota.
   * Aqui fazemos um cleanup global SOMENTE de overlays de menu/contexto no NavigationStart.
   */
  private setupPrimeNgOverlayCleanup(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationStart),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.cleanupPrimeNgMenuOverlays());
  }

  private cleanupPrimeNgMenuOverlays(): void {
    // Remove overlays específicos de menu (não mexe em calendários/dropdowns, etc.)
    const explicitSelectors = ['.p-menu-overlay', '.p-tieredmenu-overlay', '.p-contextmenu'];
    document.querySelectorAll(explicitSelectors.join(',')).forEach(el => el.remove());

    // PrimeNG 21 às vezes encapsula em .p-overlay. Removemos apenas se for overlay de menu.
    document.querySelectorAll('.p-overlay').forEach(el => {
      const hasMenuLikeContent =
        !!el.querySelector('.p-menu, .p-tieredmenu, .p-contextmenu') ||
        !!el.querySelector(
          '[data-pc-name="menu"], [data-pc-name="tieredmenu"], [data-pc-name="contextmenu"]'
        );

      if (hasMenuLikeContent) {
        el.remove();
      }
    });
  }

  loadClarityScript() {
    if (document.getElementById('clarity-script')) {
      console.info('Clarity script já adicionado');
      return;
    }

    console.info('loadClarityScript started');
    const clarityScript = document.createElement('script');
    clarityScript.type = 'text/javascript';
    clarityScript.id = 'clarity-script';
    clarityScript.innerHTML = `
      (function (c, l, a, r, i, t, y) {
        c[a] = c[a] || function () {
          (c[a].q = c[a].q || []).push(arguments);
        };
        t = l.createElement(r);
        t.async = 1;
        t.src = 'https://www.clarity.ms/tag/' + i;
        y = l.getElementsByTagName(r)[0];
        y.parentNode.insertBefore(t, y);
      })(window, document, 'clarity', 'script', '${environment.clarity.key}');
    `;
    document.head.appendChild(clarityScript);
  }

  loadGoogleAnalytics() {
    if (document.getElementById('gtag-script')) {
      console.info('Google Analytics script já adicionado');
      return;
    }

    console.info('loadGoogleAnalytics started');
    const gtagScript = document.createElement('script');
    gtagScript.async = true;
    gtagScript.src = `https://www.googletagmanager.com/gtag/js?id=${environment.googleAnalytics.trackingId}`;
    gtagScript.id = 'gtag-script';
    document.head.appendChild(gtagScript);

    if (!document.getElementById('gtag-config-script')) {
      const gtagConfigScript = document.createElement('script');
      gtagConfigScript.id = 'gtag-config-script';
      gtagConfigScript.innerHTML = `
        window.dataLayer = window.dataLayer || [];
        function gtag() {
          dataLayer.push(arguments);
        }
        gtag('js', new Date());
        gtag('config', '${environment.googleAnalytics.trackingId}');
      `;
      document.head.appendChild(gtagConfigScript);
    }
  }

  loadHotJarAnalytics() {
    console.info('loadHotJarAnalytics started');
    Hotjar.init(environment.hotjar.siteId, environment.hotjar.version);
  }

  private handleKeyboardShortcuts(event: KeyboardEvent): void {
    // ALT + 0 - Toggle Debug Info
    if (event.altKey && event.key === '0') {
      event.preventDefault();
      this.store.dispatch(toggleDebugInfo());
      console.info('Debug info toggled via keyboard shortcut (ALT + 0)');
    }

    // CTRL + F5 - Clear All Caches
    if (event.ctrlKey && event.key === 'F5') {
      this.apiService.clearAllCache();
      console.info('All local caches cleared via keyboard shortcut (CTRL + F5)');
    }
  }
}
